<template>
  <div class="app-bar white-bg">
    <div
      class="mobile-menu-overlay"
      v-if="mobileMenuOpen"
      @click="toggleMobileMenu"
    ></div>

    <div
      class="app-bar-section menu-section"
      :class="{ 'is-open': mobileMenuOpen }"
    >
      <div class="mobile-menu-header" v-if="isMobile">
        <div class="mobile-close">
          <img src="/images/close.svg" alt="Close" @click="toggleMobileMenu" />
        </div>
      </div>

      <div class="menu-item menu-link" v-if="canAccessYsaDashboard && isMobile">
        <div class="menu-title">
          <router-link :to="{ name: 'YSATeacherStudentListPage' }">
            <span>YSA Dashboard</span>
          </router-link>
        </div>
      </div>

      <div class="mobile-user-menu" v-if="isMobile">
        <a v-if="!user" class="mobile-sign-in" @click="promptSignIn">Login</a>
        <div v-else class="mobile-profile">
          <div class="mobile-profile-info">
            <img
              v-if="user.image"
              :src="user.image"
              alt=""
              class="mobile-avatar"
            />
            <div>
              <div class="mobile-user-name">
                {{ user.firstname }} {{ user.lastname }}
              </div>
              <div class="mobile-user-role">{{ user.role || "User" }}</div>
            </div>
          </div>
          <div class="mobile-menu-actions">
            <router-link
              to="/profile"
              class="mobile-menu-action"
              @click="mobileMenuOpen = false"
            >
              <img src="/images/user-square.svg" alt="" />
            </router-link>

            <div class="mobile-menu-action mobile-signout" @click="signOut">
              <img src="/images/logout.svg" alt="" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="app-bar-section user-section">
      <a
        v-if="!user"
        class="sign-in-button"
        @click="promptSignIn"
        role="button"
        tabindex="0"
        >Sign In / Sign Up</a
      >
      <div v-else class="user-profile-container">
        <div
          class="user-profile-button"
          tabindex="0"
          @click="toggleUserMenu"
          @keydown.enter="toggleUserMenu"
          role="button"
          aria-haspopup="true"
          :aria-expanded="userMenuOpen"
        >
          <img v-if="user.image" :src="user.image" alt="" class="user-avatar" />
          <span class="user-name"
            >{{ user.firstname }} {{ user.lastname }}</span
          >
          <img
            :src="
              userMenuOpen
                ? '/images/chevron-up.svg'
                : '/images/chevron-down.svg'
            "
            alt=""
            class="chevron-icon"
          />
        </div>

        <div v-if="userMenuOpen" class="custom-dropdown">
          <router-link
            to="/profile"
            class="dropdown-item"
            @click="userMenuOpen = false"
          >
            <img src="/images/user-square.svg" class="dropdown-icon" alt="" />
            <span>Profile</span>
          </router-link>

          <router-link
            v-if="canAccessYsaDashboard"
            :to="{ name: 'YSATeacherStudentListPage' }"
            class="dropdown-item"
            @click="userMenuOpen = false"
          >
            <img
              src="/images/sidebar-menu/ysa-dashboard-blue.svg"
              class="dropdown-icon"
              alt=""
            />
            <span>YSA Dashboard</span>
          </router-link>

          <div
            class="dropdown-item signout-item"
            @click="signOut"
            role="button"
            tabindex="0"
          >
            <img src="/images/logout.svg" class="dropdown-icon" alt="" />
            <span>Sign Out</span>
          </div>
        </div>
      </div>
    </div>

    <div
      class="hamburger-menu"
      @click="toggleMobileMenu"
      v-if="isMobile && !mobileMenuOpen"
    >
      <img
        src="/images/humburger-menu.svg"
        alt="Menu"
        aria-label="Toggle navigation menu"
      />
    </div>
  </div>
</template>

<script setup>
import cookiesService from "@/services/cookiesService";
import userService from "@/services/userService";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";

const user = ref(null);
const userMenuOpen = ref(false);
const mobileMenuOpen = ref(false);
const isMobile = ref(window.innerWidth < 991);
const router = useRouter();
const topSectionHeight = ref("0px");

const handleResize = () => {
  isMobile.value = window.innerWidth < 991;
};

window.addEventListener("resize", handleResize);

onMounted(() => {
  checkAuthToken();
  window.addEventListener("resize", handleResize);

  document.addEventListener("click", closeUserMenuOutside);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  document.removeEventListener("click", closeUserMenuOutside);
});

const checkAuthToken = async () => {
  try {
    const authToken = await cookiesService.getCookie("authToken");
    if (authToken) {
      const response = await userService.getProfile(authToken.token);
      const userProfile = response.data?.data;

      if (userProfile) {
        user.value = userProfile;
        await cookiesService.setCookie("authToken", {
          ...authToken,
          ...userProfile,
        });
      }

      if (user.value.user_group_id === 1) {
        topSectionHeight.value = "40px";
      }
    }
  } catch (error) {
    console.error(error);
  }
};

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value;

  if (mobileMenuOpen.value && userMenuOpen.value) {
    mobileMenuOpen.value = false;
  }
}

function closeUserMenuOutside(event) {
  const userProfileContainer = document.querySelector(
    ".user-profile-container"
  );
  if (
    userProfileContainer &&
    !userProfileContainer.contains(event.target) &&
    userMenuOpen.value
  ) {
    userMenuOpen.value = false;
  }
}

const canAccessYsaDashboard = computed(() => {
  return (
    (user.value?.role === "Teacher" || user.value?.role === "Administrator") &&
    user.value?.ysa &&
    user.value?.school?.ysa
  );
});

function signOut() {
  cookiesService.removeCookie("authToken");
  window.location.href = "/login";
}

function promptSignIn(event) {
  event.preventDefault();
  router.push("/login");
}
</script>

<style scoped>
.app-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  box-shadow: none;
  border-bottom: 1px solid #d8dae5;
  background-color: white;
  position: relative;
  z-index: 10;
}

.app-bar-section {
  display: flex;
  align-items: center;
}

.hamburger-menu {
  display: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.hamburger-menu:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.hamburger-menu:focus-visible {
  outline: 2px solid #e00069;
  outline-offset: 2px;
}

.hamburger-menu img {
  width: 24px;
  height: 24px;
  transition: transform 0.3s ease;
}

.menu-section {
  display: flex;
  flex: 1;
  justify-content: center;
  gap: 0.8em;
  position: relative;
}

.user-section {
  justify-content: end;
}

.menu-link {
  text-decoration: none;
  color: var(--primary-blue);
  font-weight: 500;
  font-family: "League Spartan";
  font-size: 0.95em;
  cursor: pointer;
  padding: 7px 10px;
  border-bottom: 3px solid transparent;
  transition:
    color 0.3s,
    border-color 0.3s;
}

.menu-title {
  display: flex;
  align-items: center;
  gap: 5px;
}

.menu-chevron {
  width: 14px;
  height: 14px;
  transition: transform 0.3s ease;
  display: inline-block;
  margin-left: 5px;
}

.chevron-rotated {
  transform: rotate(-180deg);
}

.menu-link:hover {
  border-bottom: 3px solid #e00069;
  transition:
    color 0.3s,
    border-color 0.3s;
}

.menu-item > .menu-title > a {
  text-decoration: none;
  color: var(--primary-blue);
  transition:
    color 0.3s,
    text-decoration-color 0.3s;
}

.menu-item > .menu-title > a:hover {
  color: #e00069;
  text-decoration-color: #e00069;
}

.menu-link.active {
  color: #e00069;
  border-bottom-color: #e00069;
}

.submenu-container {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  background-color: white;
  z-index: 100;
  top: 100%;
  left: 0;
  transform: translateY(10px);
  min-width: 250px;
  width: auto;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  border-radius: 0 0 8px 8px;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease,
    transform 0.3s ease;
  padding: 10px;
}

.submenu-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  background-color: white;
  border-radius: 6px;
}

.submenu {
  position: static;
  background-color: transparent;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
  min-width: 200px;
}

.submenu-link {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 5px;
  border: none;
  transition:
    background-color 0.2s,
    color 0.2s;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 500;
}

.submenu-link:hover {
  background-color: #f0f2f5;
}

.submenu-parent {
  justify-content: space-between;
}

.sub-submenu {
  visibility: hidden;
  opacity: 0;
  background-color: white;
  margin-top: 5px;
  border-radius: 6px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease,
    transform 0.3s ease;
  transform: translateY(5px);
  position: absolute;
  left: 100%;
  top: -10px;
  min-width: 200px;
  z-index: 110;
}

.sub-submenu-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 4px;
  transition:
    background-color 0.2s,
    color 0.2s;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 500;
}

.sub-submenu-link:hover {
  background-color: #f0f2f5;
}

.arrow-icon {
  width: 12px;
  height: 12px;
  margin-left: auto;
  transition: transform 0.3s ease;
}

/* Mobile screen styles */
@media (max-width: 991px) {
  .user-section {
    display: none;
  }

  .app-bar {
    padding: 12px 16px;
    position: relative;
    z-index: 50;
  }

  .hamburger-menu {
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 101;
  }

  .menu-section {
    display: none;
  }

  .menu-section.is-open {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    background-color: white;
    z-index: 100;
    overflow-y: auto;
    box-shadow: -2px 0 15px rgba(0, 0, 0, 0.2);
    animation: slideIn 0.3s ease-out;
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  .mobile-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99;
    backdrop-filter: blur(2px);
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .mobile-menu-header {
    width: 100%;
    display: flex;
    padding: 10px 10px 0 0;
    justify-content: flex-end;
    border-bottom: 1px solid #eee;
  }

  .mobile-close {
    cursor: pointer;
    padding: 8px;
  }

  .mobile-close img {
    width: 20px;
    height: 20px;
  }

  .menu-link {
    font-size: var(--fs-medium);
    padding: 15px 20px;
    border-bottom: 1px solid #eee;
    border-left: none;
    margin: 0;
  }

  .menu-link.active {
    background-color: rgba(0, 61, 106, 0.05);
    color: var(--primary-blue);
    border-left: 4px solid var(--primary-blue);
  }

  .menu-item {
    width: 100%;
    border-bottom: none;
  }

  .menu-title {
    justify-content: space-between;
    width: 100%;
  }

  .submenu-container {
    display: none;
    visibility: visible;
    opacity: 1;
    position: static;
    box-shadow: none;
    width: 100%;
    padding: 0;
    margin: 0;
    border-radius: 0;
    transition: max-height 0.3s ease;
    background-color: #f9f9f9;
  }

  .submenu-container.mobile-submenu-open {
    display: block;
  }

  .submenu-wrapper {
    padding: 0;
    flex-direction: column;
    max-width: unset;
    border-radius: 0;
  }

  .submenu-link {
    padding: 12px 15px 12px 35px;
    margin: 0;
    border-radius: 0;
    border-bottom: 1px solid #eee;
    font-size: 15px;
  }

  .sub-submenu {
    display: none;
    visibility: visible;
    opacity: 1;
    position: static;
    box-shadow: none;
    width: 100%;
    padding: 0;
    margin: 0;
    background-color: #f0f0f0;
  }

  .sub-submenu.mobile-sub-open {
    display: block;
  }

  .sub-submenu-link {
    padding: 12px 15px 12px 50px;
    margin: 0;
    border-radius: 0;
    border-bottom: 1px solid #e5e5e5;
    font-size: var(--fs-small);
  }

  .mobile-user-menu {
    margin-top: auto;
    width: 100%;
    padding: 20px;
    border-top: 1px solid #eee;
  }

  .mobile-sign-in {
    display: block;
    background-color: var(--primary-blue);
    color: white;
    text-align: center;
    padding: 12px;
    border-radius: 6px;
    font-weight: 500;
    text-decoration: none;
  }

  .mobile-profile {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .mobile-profile-info {
    display: flex;
    align-items: center;
    gap: 12px;
    border-bottom: 1px solid #eee;
  }

  .mobile-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
  }

  .mobile-user-name {
    font-weight: 500;
    color: var(--primary-blue);
  }

  .mobile-user-role {
    font-size: 13px;
    color: #666;
  }

  .mobile-menu-actions {
    display: flex;
  }

  .mobile-menu-action {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    border-radius: 6px;
    color: var(--primary-blue);
    text-decoration: none;
    transition: background-color 0.2s;
  }

  .mobile-menu-action:hover {
    background-color: #f0f0f0;
  }

  .mobile-menu-action img {
    width: 20px;
    height: 20px;
  }

  .mobile-signout {
    color: #e00069;
  }
}

.margin-label {
  margin-top: 0.23em;
}

.submenu-link img.colour-image,
.sub-submenu-link img.colour-image {
  filter: invert(16%) sepia(22%) saturate(6604%) hue-rotate(187deg)
    brightness(99%) contrast(101%);
}

.sign-in-button {
  padding: 8px 16px;
  border-radius: 20px;
  transition: background-color 0.3s;
  margin-top: 0;
}

.sign-in-button:hover {
  color: #e00069;
}

.user-profile-container {
  position: relative;
}

.user-profile-button {
  font-family: "League Spartan";
  display: flex;
  align-items: center;
  font-weight: 500;
  color: var(--primary-blue);
  gap: 12px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 20px;
  transition: background-color 0.3s;
}

.user-profile-button:hover,
.user-profile-button:focus {
  background-color: rgba(0, 0, 0, 0.05);
  outline: none;
}

.user-avatar {
  height: 30px;
  width: 30px;
  border-radius: 50%;
  object-fit: cover;
}

.user-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.chevron-icon {
  height: 12px;
  width: 12px;
  transition: transform 0.3s;
}

.custom-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 8px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  font-family: "League Spartan";
  display: flex;
  align-items: center;
  font-weight: 500;
  color: var(--primary-blue);
  gap: 12px;
  cursor: pointer;
  padding: 12px 16px;
  text-decoration: none;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.dropdown-icon {
  height: 20px;
  width: 20px;
}

.signout-item {
  color: #e00069;
}

@media (min-width: 992px) {
  .menu-item {
    position: relative;
  }

  .menu-item:hover .submenu-container {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }

  .submenu-container:hover {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }

  .menu-item:hover > .menu-title a,
  .menu-item:hover > .menu-title {
    color: #e00069;
  }

  .menu-item:hover > .menu-link {
    border-bottom-color: #e00069;
  }

  .submenu-item {
    position: relative;
  }

  .submenu-item:hover .sub-submenu {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }

  .sub-submenu:hover {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }

  .submenu-item:hover > a.submenu-parent {
    background-color: #f0f2f5;
    color: #e00069;
  }

  .submenu-item:hover > a.submenu-parent img.colour-image {
    filter: invert(34%) sepia(98%) saturate(2461%) hue-rotate(315deg)
      brightness(91%) contrast(95%);
  }
}
</style>
